import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import ReactDOMServer from "react-dom/server";
import { useEffect, useState } from "react";
import { AuthDealersPinIcon } from "components/Icons";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useLoggedIn } from "stores/auth/authStore";
import { useEventStore } from "stores/event/useEventStore";
import { Modal, useModalStore } from "stores/modal/useModalStore";

dayjs.extend(utc);
dayjs.extend(tz);

const iconUrl = `data:image/svg+xml,${encodeURIComponent(
  ReactDOMServer.renderToString(<AuthDealersPinIcon />)
)}`;

/**
 * Format an event date for display on the intended calendar day.
 * Date-only strings ('YYYY-MM-DD') already name the day — format as-is.
 * Full ISO datetimes must be shifted into the event's timezone (matching
 * BookingModal) or the label can disagree with the booking picker.
 */
const formatEventDay = (value, timezone) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? dayjs(value).format("MMM DD YYYY")
    : dayjs(value).tz(timezone || "America/Chicago").format("MMM DD YYYY");

export const EventsMarker = ({
  map,
  eventInfoWindowRef,
}) => {
  const { user, setIsAction } = useLoggedIn();

  const open = useModalStore((s) => s.open);
  const eventToggle = useEventStore((state) => state.eventToggle);
  const activeMarker = useEventStore((state) => state.activeMarker);
  const eventsData = useEventStore((state) => state.eventsData);
  const eventTypeFilter = useEventStore((state) => state.eventTypeFilter);
  const markerCluster = useEventStore((state) => state.markerCluster);

  const setOpenModalIndex = useEventStore((action) => action.setOpenModalIndex);
  const setPendingBookingIndex = useEventStore((action) => action.setPendingBookingIndex);
  const setSelectedEvents = useEventStore((action) => action.setSelectedEvents);
  const setMarkerCluster = useEventStore((action) => action.setMarkerCluster);
  const setActiveMarker = useEventStore((action) => action.setActiveMarker);

  /** Use coordinates from listEvents when present; legacy Dynamo rows store them as strings (nulled per-item via errorPolicy "all"), so geocode eventLocation as the fallback. */
  const [mapEvents, setMapEvents] = useState([]);

  useEffect(() => {
    if (!eventsData?.length) {
      setMapEvents([]);
      return;
    }
    // Wait for Google Map instance — Geocoder API loads with the map script.
    if (!map) {
      return;
    }
    let cancelled = false;
    const google = window.google;
    if (!google?.maps?.Geocoder) {
      setMapEvents(eventsData);
      return;
    }
    const geocoder = new google.maps.Geocoder();

    Promise.all(
      eventsData.map((event) => {
        const lat = event.coordinates?.latitude;
        const lng = event.coordinates?.longitude;
        if (
          typeof lat === "number" &&
          typeof lng === "number" &&
          !Number.isNaN(lat) &&
          !Number.isNaN(lng)
        ) {
          return Promise.resolve(event);
        }
        const addr = (event.eventLocation || "").trim();
        if (!addr) {
          return Promise.resolve(event);
        }
        return new Promise((resolve) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            if (cancelled) {
              resolve(event);
              return;
            }
            if (status === "OK" && results?.[0]) {
              const loc = results[0].geometry.location;
              resolve({
                ...event,
                coordinates: {
                  latitude: loc.lat(),
                  longitude: loc.lng(),
                  __typename: "Coordinates",
                },
              });
            } else {
              resolve(event);
            }
          });
        });
      })
    ).then((merged) => {
      if (!cancelled) setMapEvents(merged);
    });

    return () => {
      cancelled = true;
    };
  }, [eventsData, map]);

  useEffect(() => {
    if (!map || !mapEvents) return;

    const google = window.google;

    const markers = mapEvents
      ?.filter(
        (event) =>
          event.coordinates?.latitude &&
          event.coordinates?.longitude &&
          event.visibility === "ENABLED" &&
          (!eventTypeFilter || event.eventType === eventTypeFilter)
      )
      ?.map(
        (event, index) =>
          new google.maps.Marker({
            position: {
              lat: event.coordinates.latitude,
              lng: event.coordinates.longitude,
            },
            title: event?.eventName,
            image: event?.image,
            map: eventToggle ? map : null,
            icon: iconUrl,
            customInfo: index,
          })
      );

    markers.forEach((marker) => {
      marker.addListener("click", () => {
        if (activeMarker === marker) {
          eventInfoWindowRef.current.close();
          setActiveMarker(null);
        } else {
          const elementsAtLocation = mapEvents
            ?.map((el, idx) => ({ ...el, originalIndex: idx }))
            ?.filter(
              (el) =>
                el.coordinates?.latitude === marker.getPosition().lat() &&
                el.coordinates?.longitude === marker.getPosition().lng()
            );

          const eventContent = elementsAtLocation
            ?.map((el) =>
              ReactDOMServer.renderToString(
                eventToggle && (
                  <div
                    key={el.originalIndex}
                    className="card-dashboard !h-auto !w-auto gap-x-4"
                  >
                    <div className="card_head-dashboard">
                      <h1
                        title={el.eventName}
                        className="card-text-dashboard truncate text-wrap !w-[210px]"
                      >
                        {el.eventName}
                      </h1>
                      {el.eventType ? (
                        <p className="scrollable-card-text-date !h-auto !text-[11px] opacity-90">
                          {el.eventType}
                        </p>
                      ) : null}
                      <p className="scrollable-card-text-date !h-auto">
                        {`${el.city}, ${el.state}`}
                      </p>
                      <p className="scrollable-card-text-date !h-auto">
                        {formatEventDay(el.startDate, el.timezone)}
                        {el.endDate && el.endDate !== el.startDate ? (
                          <span> - {formatEventDay(el.endDate, el.timezone)}</span>
                        ) : null}
                      </p>
                      <button
                        className="dashboard-card-btn bg-primary book-now-button"
                        data-index={el.originalIndex}
                      >
                        Book now
                      </button>
                    </div>
                    <div className="flex items-center">
                      <img
                        src={el.image}
                        alt="eventImage"
                        className="card_img_dashboard cursor-pointer px-1 py-1 mr-2 h-[150px] w-[140px] object-cover"
                      />
                      <img
                        src={el.image}
                        alt="eventImage"
                        className="mobile_card_img mr-2 px-1 py-1 cursor-pointer object-cover"
                      />
                    </div>
                  </div>
                )
              )
            )
            .join("");

          const scrollableContent = `
            <div style="max-height: 250px; overflow-y: auto;">
              ${eventContent}
            </div>
          `;

          if (eventInfoWindowRef.current) {
            eventInfoWindowRef.current.setContent(scrollableContent);
            eventInfoWindowRef.current.open(map, marker);
            setActiveMarker(marker);

            google.maps.event.addListenerOnce(
              eventInfoWindowRef.current,
              "domready",
              () => {
                const buttons = document.querySelectorAll(".book-now-button");
                buttons.forEach((button) => {
                  button.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const index = parseInt(button.getAttribute("data-index"), 10);
                    window.dispatchEvent(
                      new CustomEvent("bookNowClick", {
                        detail: { index },
                      })
                    );
                  });
                });
              }
            );
          }
        }
      });
    });

    let markerClusterInstance = null;
    if (eventToggle) {
      markerClusterInstance = new MarkerClusterer({
        map: map,
        markers: markers,
      });
      setMarkerCluster(markerClusterInstance);
    } else {
      markers.forEach((marker) => marker.setMap(null));
      if (markerCluster) {
        markerCluster.clearMarkers();
      }
    }

    const handleMapClick = () => {
      if (eventInfoWindowRef.current) {
        eventInfoWindowRef.current.close();
        setActiveMarker(null);
      }
    };

    const mapClickListener = map.addListener("click", handleMapClick);

    return () => {
      markers.forEach((marker) => marker.setMap(null));
      if (markerClusterInstance) {
        markerClusterInstance.clearMarkers();
      }
      google.maps.event.removeListener(mapClickListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapEvents, eventToggle, eventInfoWindowRef, setMarkerCluster, eventTypeFilter]);

  useEffect(() => {
    const handleBookNow = (e) => {
      const index = parseInt(e.detail.index, 10);
      const event = mapEvents[index] ?? eventsData[index];
      const eventList = event ? [event] : [];

      setIsAction("BookEvent");
      if (user) {
        setOpenModalIndex(index);
        setSelectedEvents(eventList);
      } else {
        open(Modal.Login);
        setPendingBookingIndex(index);
        setSelectedEvents(eventList);
      }
    };

    window.addEventListener("bookNowClick", handleBookNow);
    return () => window.removeEventListener("bookNowClick", handleBookNow);
  }, [user, setIsAction, setOpenModalIndex, setPendingBookingIndex, eventsData, mapEvents, setSelectedEvents, open]);

  return null;
};
